/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Wallet, Percent, ShieldCheck, Trash2, Calculator,
  Clock, XCircle, CheckCircle2,
} from 'lucide-react';
import {
  getPricingTiers, getMyPricingStatus, declareIsee, clearIsee, getPricingQuote,
} from '@/lib/pricing';
import { IseeAttestazioneUpload } from '@/components/growth/IseeAttestazioneUpload';
import { FamilyBundleSection } from '@/components/growth/FamilyBundleSection';

/**
 * Sprint 9E + 9F — Sliding scale ISEE + Bundle famiglia + Verification hook.
 * Solo pazienti.
 *
 * Refactor note (Code Quality): l'upload dell'attestazione e la sezione
 * bundle famiglia vivono in componenti dedicati (`IseeAttestazioneUpload`,
 * `FamilyBundleSection`).
 */
const VERIFICATION_META = {
  pending: { Icon: Clock, cls: 'bg-amber-500/15 text-amber-600', label: 'In verifica' },
  verified: { Icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-600', label: 'Verificato' },
  rejected: { Icon: XCircle, cls: 'bg-red-500/15 text-red-600', label: 'Rigettato' },
};

const IseeBundlePanel = ({ isPatient = true }) => {
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState(null);
  const [status, setStatus] = useState(null);
  const [quote, setQuote] = useState(null);

  const [iseeValue, setIseeValue] = useState('');
  const [iseeConsent, setIseeConsent] = useState(true);
  const [savingIsee, setSavingIsee] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [tr, st, qt] = await Promise.all([
        getPricingTiers(),
        getMyPricingStatus(),
        getPricingQuote(30).catch(() => null),
      ]);
      setTiers(tr);
      setStatus(st);
      setQuote(qt);
      if (st?.isee?.isee_value != null) setIseeValue(String(st.isee.isee_value));
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isPatient) refresh(); else setLoading(false); }, [isPatient, refresh]);

  if (!isPatient) return null;

  const handleDeclareIsee = async () => {
    const val = parseFloat(iseeValue);
    if (isNaN(val) || val < 0) return toast.error('Valore ISEE non valido');
    if (!iseeConsent) return toast.error('Serve il consenso all\'autodichiarazione');
    setSavingIsee(true);
    try {
      await declareIsee(val, true, true);
      toast.success('ISEE autodichiarato salvato');
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
    finally { setSavingIsee(false); }
  };

  const handleClearIsee = async () => {
    try { await clearIsee(); toast.success('ISEE rimosso'); setIseeValue(''); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  if (loading) return <Skeleton className="h-64 rounded-3xl" data-testid="pricing-flex-loading" />;

  const iseeState = status?.isee;
  const vStatus = iseeState?.verification_status;
  const vMeta = vStatus ? VERIFICATION_META[vStatus] : null;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      data-testid="isee-bundle-panel">
      <Card className="rounded-3xl border-primary/25 overflow-hidden">
        <CardHeader className="bg-gradient-to-br from-primary/10 via-card to-card">
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="h-5 w-5 text-primary" />
            Prezzo flessibile
          </CardTitle>
          <CardDescription>
            Sconti su misura per te e la tua famiglia. Autodichiarazione, verifica reale post-accreditamento.
          </CardDescription>
          {quote && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="mt-3 flex flex-wrap items-center gap-2" data-testid="pricing-quote-strip">
              <Badge variant="outline" className="gap-1"><Calculator className="h-3 w-3" /> Esempio consulto 30 €</Badge>
              <Badge className="bg-primary/15 text-primary border-0 gap-1"><Percent className="h-3 w-3" /> –{quote.discount_pct}%</Badge>
              <Badge className="bg-emerald-500/15 text-emerald-600 border-0">Finale: €{quote.final_price.toFixed(2)}</Badge>
            </motion.div>
          )}
        </CardHeader>
        <CardContent className="space-y-6 pt-5">
          {/* ---------- ISEE Section ---------- */}
          <section data-testid="isee-section">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm">Sliding scale ISEE (autodichiarato)</h3>
              {iseeState?.tier_label && (
                <Badge variant="outline" className="ml-auto" data-testid="isee-current-tier">
                  {iseeState.tier_label} – {iseeState.discount_pct}%
                </Badge>
              )}
              {vMeta && (
                <Badge className={`${vMeta.cls} border-0 gap-1`} data-testid="isee-verification-badge">
                  <vMeta.Icon className="h-3 w-3" /> {vMeta.label}
                </Badge>
              )}
            </div>
            <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
              <div>
                <Label htmlFor="isee-input" className="text-xs">Valore ISEE (€)</Label>
                <Input id="isee-input" type="number" inputMode="decimal" min="0" step="100"
                  value={iseeValue} onChange={(e) => setIseeValue(e.target.value)}
                  placeholder="es. 18500" data-testid="isee-value-input" />
              </div>
              <Button onClick={handleDeclareIsee} disabled={savingIsee}
                className="self-end" data-testid="isee-save-btn">
                {savingIsee ? 'Attendere…' : 'Salva'}
              </Button>
              {iseeState && (
                <Button variant="ghost" size="icon" onClick={handleClearIsee} title="Rimuovi"
                  className="self-end text-destructive hover:text-destructive" data-testid="isee-clear-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Checkbox id="isee-consent" checked={iseeConsent} onCheckedChange={setIseeConsent} data-testid="isee-consent" />
              <label htmlFor="isee-consent" className="text-xs text-muted-foreground leading-snug">
                Dichiaro sotto la mia responsabilità che i dati ISEE inseriti sono veritieri. Potrò essere chiamato a fornire l'attestazione ISEE originale.
              </label>
            </div>

            {/* ---- Attestazione Upload (Sprint 9F) ---- */}
            {iseeState && <IseeAttestazioneUpload iseeState={iseeState} onChanged={refresh} />}

            {tiers?.isee_tiers && (
              <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
                {tiers.isee_tiers.map((t2) => {
                  const active = iseeState?.tier_id === t2.id;
                  return (
                    <div key={t2.id}
                      className={`flex items-center justify-between rounded-xl border px-3 py-1.5 text-xs ${active ? 'bg-primary/10 border-primary/40' : 'bg-secondary/30 border-border/60'}`}
                      data-testid={`isee-tier-${t2.id}`}>
                      <span>{t2.label}</span>
                      <span className="font-semibold">–{t2.discount_pct}%</span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ---------- Family Bundle Section (Sprint 9E) ---------- */}
          <FamilyBundleSection tiers={tiers} status={status} onRefresh={refresh} />
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default IseeBundlePanel;
